/**
 * `repeater` — toistuva kenttäryhmä (WP-HV5, päätös D6 = A).
 *
 * <p>Tämä moduuli sisältää repeaterin PUHTAAT apurit: rividatan normalisoinnin,
 * id-skooppauksen ja rivin esitäytön. Itse kytkentä (kloonaus, sidontojen
 * rekisteröinti, lisää/poista) on `runtime.ts`:ssä, koska se tarvitsee
 * storen ja uudelleenajettavat kytkentäfunktiot.
 *
 * <p><b>Miksi id:t skoopataan.</b> Sama lapsikomponentti renderöityy kerran per
 * rivi, joten sen `data-app-id` esiintyisi elävässä DOMissa N kertaa. `byAppId`
 * palauttaisi aina ensimmäisen, jolloin rivin 3 sidonta kirjoittaisi rivin 0
 * elementtiin. Skooppaus (`total` → `total__r3`) tekee jokaisesta rivistä oman
 * nimiavaruutensa, ja rivin sidonnat käännetään samaan avaruuteen
 * {@link rewriteScopedIds}illä.
 *
 * <p>Lapset renderöidään palvelimella `<template>`-elementtiin. Se on
 * tarkoituksellista: templaten sisältö ei ole osa DOM-puuta, joten
 * `querySelector` ei näe sitä eikä skooppaamattomia id:itä ole olemassa
 * hetkeäkään.
 */

/** Rivin nimiavaruuden tunnus lausekkeissa (`${row.dayName}`). */
export const ROW_SCOPE_NAME = 'row';

/** Skoopatun id:n muoto: `<id>__r<index>`. */
export function scopedId(id: string, index: number): string {
  return `${id}__r${index}`;
}

/** Rivin datan tilatunnus storessa — `row` mäpätään tähän. */
export function rowStateId(repeaterId: string, index: number): string {
  return `${repeaterId}__row${index}`;
}

/**
 * Normalisoi `items`-arvo riviobjekteiksi.
 *
 * <p>Ei-objektialkiot (merkkijono, luku) kääritään muotoon `{ value: x }`, jotta
 * `${row.value}` toimii myös paljaalla listalla — hiljainen pudotus olisi juuri
 * se väärä-tyhjä-tulos jota nämä löydökset muutenkin metsästävät. `null` ja
 * `undefined` alkioina muuttuvat tyhjäksi riviksi, koska rivi on olemassa
 * silloinkin kun sen data puuttuu.
 */
export function parseRepeaterRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (item === null || item === undefined) return {};
    if (typeof item === 'object' && !Array.isArray(item)) {
      return item as Record<string, unknown>;
    }
    return { value: item };
  });
}

/**
 * Rivin tunnisteen arvo, kun repeaterille on annettu `rowKeyPath`. Käytetään
 * vain rivin poistoon ja järjestyksen säilymiseen; puuttuva avain → index.
 */
export function rowKeyOf(row: Record<string, unknown>, keyPath: string, index: number): string {
  if (!keyPath) return String(index);
  const v = row[keyPath];
  return v === null || v === undefined || v === '' ? String(index) : String(v);
}

/**
 * Kerää klooniin jääneet komponentti-id:t. Luetaan ENNEN uudelleenkirjoitusta,
 * koska mäppäys rakennetaan alkuperäisistä nimistä.
 */
export function collectScopeIds(fragment: ParentNode): string[] {
  const ids: string[] = [];
  fragment.querySelectorAll('[data-app-id]').forEach((el) => {
    const id = el.getAttribute('data-app-id');
    if (id) ids.push(id);
  });
  return ids;
}

/**
 * Skooppaa kloonin id:t ja palauta lausekkeiden uudelleenkirjoitusmäppäys.
 * Mäppäykseen tulee myös `row` → rivin tilatunnus, jolloin `${row.x}` osuu
 * storeen ilman erillistä evaluointikontekstia.
 */
export function applyRowScope(
  fragment: ParentNode,
  repeaterId: string,
  index: number,
): Map<string, string> {
  const mapping = new Map<string, string>();
  for (const id of collectScopeIds(fragment)) {
    mapping.set(id, scopedId(id, index));
  }
  fragment.querySelectorAll('[data-app-id]').forEach((el) => {
    const id = el.getAttribute('data-app-id');
    const next = id ? mapping.get(id) : undefined;
    if (next) el.setAttribute('data-app-id', next);
  });
  scopeDomIds(fragment, index);
  // `row` viimeisenä: se ei ole komponentti-id eikä saa jäädä yliajetuksi
  // siinäkään tapauksessa että joku on nimennyt komponenttinsa "row"ksi —
  // rividatan on voitettava, muuten rivikohtaiset otsikot lakkaisivat toimimasta.
  mapping.set(ROW_SCOPE_NAME, rowStateId(repeaterId, index));
  return mapping;
}

/**
 * Skooppaa kloonin DOM-id:t ja niihin osoittavat viittaukset (`for`,
 * `aria-labelledby`). Renderöijä johtaa label-kytkennän id:t solmun
 * id:stä (`email-input`), joten ilman tätä jokainen rivi toistaisi samat id:t:
 * labelin klikkaus kohdistaisi aina rivin 0 kentän ja ruudunlukija nimeäisi
 * ryhmän ensimmäisen rivin otsikolla. Vain kloonin sisäiset viittaukset
 * kirjoitetaan uudelleen — ulos osoittava id jää ennalleen.
 */
function scopeDomIds(fragment: ParentNode, index: number): void {
  const renamed = new Map<string, string>();
  fragment.querySelectorAll('[id]').forEach((el) => {
    const next = scopedId(el.id, index);
    renamed.set(el.id, next);
    el.id = next;
  });
  if (renamed.size === 0) return;
  fragment.querySelectorAll('[for]').forEach((el) => {
    const next = renamed.get(el.getAttribute('for') || '');
    if (next) el.setAttribute('for', next);
  });
  fragment.querySelectorAll('[aria-labelledby]').forEach((el) => {
    const refs = (el.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
    el.setAttribute('aria-labelledby', refs.map((ref) => renamed.get(ref) ?? ref).join(' '));
  });
}

/**
 * Esitäytä rivin syötteet rividatasta: kentän `data-app-input-name` luetaan
 * rivin samannimisestä avaimesta. Tuntematon avain jätetään rauhaan (kentän
 * oma `defaultValue` jää voimaan) — tyhjentäminen olisi datan hävittämistä
 * sillä perusteella ettei sitä satu olemaan.
 */
export function presetRowInputs(fragment: ParentNode, row: Record<string, unknown>): void {
  fragment.querySelectorAll('[data-app-input-name]').forEach((el) => {
    const name = el.getAttribute('data-app-input-name') || '';
    if (!name || !(name in row)) return;
    const value = row[name];
    if (el instanceof HTMLInputElement) {
      // Monivalinta (WP-RP1): ryhmän rastit jakavat nimen ja arvo on taulukko —
      // ennen checkbox-haaraa, joka tulkitsisi taulukon yhdeksi booleaniksi.
      if (el.classList.contains('sp-multi-input')) {
        el.checked = Array.isArray(value) && value.map(String).includes(el.value);
        return;
      }
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = el.type === 'checkbox' ? Boolean(value) : String(value) === el.value;
      } else if (el.type !== 'file') {
        el.value = value === null || value === undefined ? '' : String(value);
      }
      return;
    }
    if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
      el.value = value === null || value === undefined ? '' : String(value);
    }
  });
}

/**
 * Lue rivin syötteiden NYKYISET arvot nimen mukaan — {@link presetRowInputs}in pari
 * (WP-RP1, päätös D1 = A). Rivit rakennetaan uudelleen jokaisessa lisäyksessä ja
 * poistossa, ja ennen tätä ne esitäytettiin alkuperäisestä rividatasta: käyttäjän
 * kirjoittamat arvot katosivat kaikilta riveiltä.
 *
 * <p>Arvot samassa muodossa kuin lomakkeen payloadissa: numero lukuna tai `null`,
 * checkbox booleanina, monivalinta taulukkona, radioryhmä valitun arvona (tai `''`).
 * Tiedostokentät ohitetaan: niiden arvo elää komponentin `files`-slotissa, ei
 * syötteessä. Sisäkkäisen repeaterin kentät ohitetaan (D4 = B kieltää sen).
 */
export function readRowInputs(row: ParentNode): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  row.querySelectorAll<HTMLElement>('[data-app-input-name]').forEach((el) => {
    const name = el.getAttribute('data-app-input-name') || '';
    if (!name) return;
    if (el instanceof HTMLInputElement) {
      if (el.type === 'file') return;
      if (el.classList.contains('sp-multi-input')) {
        const list = Array.isArray(values[name]) ? values[name] as string[] : [];
        if (el.checked) list.push(el.value);
        values[name] = list;
        return;
      }
      if (el.type === 'radio') {
        if (el.checked) values[name] = el.value;
        else if (!(name in values)) values[name] = '';
        return;
      }
      if (el.type === 'checkbox') {
        values[name] = el.checked;
        return;
      }
      if (el.type === 'number') {
        const t = el.value.trim();
        const n = t === '' ? null : Number(t);
        values[name] = n !== null && Number.isFinite(n) ? n : null;
        return;
      }
      values[name] = el.value;
      return;
    }
    if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
      values[name] = el.value;
    }
  });
  return values;
}

/**
 * Kuinka monta riviä näytetään kun `items` ei ole sidottu tai on tyhjä.
 * `minRows` takaa että lomake ei ole tyhjä ruutu ilman yhtään kenttää.
 */
export function padToMinRows(
  rows: Record<string, unknown>[],
  minRows: number,
): Record<string, unknown>[] {
  if (rows.length >= minRows) return rows;
  const padded = rows.slice();
  while (padded.length < minRows) padded.push({});
  return padded;
}
